import {
  GoogleAuthProvider,
  signInWithPopup,
  browserLocalPersistence,
  setPersistence,
  signOut,
} from 'firebase/auth';
import { auth } from './firebaseAuth';
import { getUserData, createUserIfNotExists } from './userService';
import { toast } from 'react-toastify';
import notificationStore from '../stores/notificationStore';
import { resetLauncherIconSession } from './homeLauncher/launcherIconCache';
import { clearAllAskSbConversations } from './askSb/conversationMemory';
import { clearSupportedTenantSession } from '../pages/Admin/supportedTenantsSession';
import { clearDealerAdminSession } from '../pages/Admin/dealerAdminSession';
import { resetAlgoAccess } from '../pages/Admin/algoDeviceSupport';

// Configure persistence
setPersistence(auth, browserLocalPersistence);

// Google auth provider
const googleProvider = new GoogleAuthProvider();

/**
 * Sign in with Google
 * @returns {Promise<{user, claims}>} - User object and custom claims
 */
export const signInWithGoogle = async () => {
  console.log('Attempting Google sign-in...');
  try {
    const result = await signInWithPopup(auth, googleProvider);
    const user = result.user;
    console.log('Google sign-in successful:', user.uid);

    // Create user document in Firestore if it doesn't exist
    console.log('Creating/updating user in Firestore...');
    await createUserIfNotExists(user);

    // Get the user's ID token with fresh claims
    console.log('Fetching fresh ID token with claims...');
    const idTokenResult = await user.getIdTokenResult(true);
    const claims = idTokenResult.claims;
    console.log('Retrieved custom claims:', claims);

    // Log success
    toast.success('Signed in successfully');
    return { user, claims };
  } catch (error) {
    console.error('Error signing in with Google:', error);

    // Show user-friendly error message
    let errorMessage = 'Failed to sign in with Google';
    if (error.code === 'auth/popup-closed-by-user') {
      errorMessage = 'Sign-in popup was closed. Please try again.';
    } else if (error.code === 'auth/cancelled-popup-request') {
      errorMessage = 'Multiple popup requests were cancelled.';
    } else if (error.code === 'auth/network-request-failed') {
      errorMessage = 'Network error. Please check your connection.';
    } else if (error.code) {
      errorMessage = `Auth error: ${error.code}`;
    }

    toast.error(errorMessage);
    throw error;
  }
};

/**
 * Sign out the current user
 */
export const logOut = async () => {
  console.log('Attempting to sign out...');
  try {
    clearSupportedTenantSession();
    clearDealerAdminSession();
    resetAlgoAccess();
    await signOut(auth);
    clearAllAskSbConversations();
    resetLauncherIconSession();
    // Pending notifications are cached locally to survive a reload; they belong to
    // the user who signed in, so the next person at this browser must not inherit
    // their prompts.
    notificationStore.clear();
    console.log('User signed out successfully');
    toast.success('Signed out successfully');
    return true;
  } catch (error) {
    console.error('Error signing out:', error);
    toast.error('Failed to sign out: ' + error.message);
    throw error;
  }
};

/**
 * Get updated custom claims from ID token
 * @param {Object} user - Firebase auth user object
 * @returns {Promise<Object>} - Custom claims object
 */
export const getUpdatedClaims = async user => {
  if (!user) {
    console.log('No user provided to getUpdatedClaims');
    return null;
  }

  console.log(`Fetching updated claims for user: ${user.uid}`);
  try {
    // Force token refresh to get latest claims
    const idTokenResult = await user.getIdTokenResult(true);
    console.log('Updated claims retrieved:', idTokenResult.claims);
    return idTokenResult.claims;
  } catch (error) {
    console.error('Error getting updated claims:', error);
    toast.error('Failed to refresh user permissions');
    return null;
  }
};
